// src/features/business-functions/hooks/useWorkspaceTableTabs.js
import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL-synced tab navigation where each workspace table is a top-level tab.
 * Active tab is stored as ?tableId=<formId>
 */
export default function useWorkspaceTableTabs(tables = []) {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabConfig = useMemo(
    () => tables.map((table) => ({
      key: String(table.id),
      label: table.name,
    })),
    [tables]
  );

  const validTableIds = useMemo(() => tabConfig.map((tab) => tab.key), [tabConfig]);
  const urlTableId = searchParams.get('tableId');

  const activeTableId = validTableIds.includes(urlTableId)
    ? urlTableId
    : (validTableIds[0] ?? false);

  const activeTable = useMemo(
    () => tables.find((table) => String(table.id) === activeTableId) ?? null,
    [tables, activeTableId]
  );

  useEffect(() => {
    if (!validTableIds.length) return;

    if (!urlTableId || !validTableIds.includes(urlTableId)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tableId', validTableIds[0]);
          next.delete('tab');
          return next;
        },
        { replace: true }
      );
    }
  }, [urlTableId, validTableIds, setSearchParams]);

  const handleTabChange = useCallback(
    (_event, newTableId) => {
      if (!newTableId || newTableId === activeTableId) return;

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tableId', newTableId);
          next.delete('tab');
          return next;
        },
        { replace: true }
      );
    },
    [activeTableId, setSearchParams]
  );

  return {
    tabConfig,
    activeTableId,
    activeTable,
    handleTabChange,
    hasTableTabs: tabConfig.length > 0,
  };
}
